import clsx from "clsx";
import PropTypes from "prop-types";
import * as React from "react";
import { Field } from "@base-ui/react/field";
import { NumberField } from "@base-ui/react/number-field";
import css from "./NumberInput.module.css";

const ChevronUpIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path
      d="M2.5 7.5L6 4l3.5 3.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronDownIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path
      d="M2.5 4.5L6 8l3.5-3.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const NumberInput = React.forwardRef(
  (
    {
      bare = false,
      value,
      defaultValue,
      onValueChange,
      min,
      max,
      step,
      smallStep,
      largeStep,
      format,
      locale,
      allowWheelScrub = false,
      allowOutOfRange = false,
      steppers = true,
      label,
      description,
      error,
      disabled = false,
      required = false,
      readOnly = false,
      name,
      className,
      ...rest
    },
    ref,
  ) => {
    const hasError = Boolean(error);

    const numberFieldContent = (
      <NumberField.Root
        value={value}
        defaultValue={defaultValue}
        onValueChange={onValueChange}
        min={min}
        max={max}
        step={step}
        smallStep={smallStep}
        largeStep={largeStep}
        format={format}
        locale={locale}
        allowWheelScrub={allowWheelScrub}
        allowOutOfRange={allowOutOfRange}
        disabled={disabled}
        required={required}
        readOnly={readOnly}
        name={name}
        {...rest}
      >
        <NumberField.Group className={css.group}>
          <NumberField.Input
            ref={ref}
            className={css.input}
            inputMode={format?.style === "currency" ? "decimal" : undefined}
          />
          {steppers && (
            <div className={css.steppers}>
              <NumberField.Increment
                className={css.stepBtn}
                aria-label="Increase"
              >
                <ChevronUpIcon />
              </NumberField.Increment>
              <NumberField.Decrement
                className={css.stepBtn}
                aria-label="Decrease"
              >
                <ChevronDownIcon />
              </NumberField.Decrement>
            </div>
          )}
        </NumberField.Group>
      </NumberField.Root>
    );

    if (bare) {
      return numberFieldContent;
    }

    return (
      <Field.Root
        className={clsx(css.root, className)}
        disabled={disabled}
        invalid={hasError}
      >
        {label && <Field.Label className={css.label}>{label}</Field.Label>}
        {numberFieldContent}
        {description && !hasError && (
          <Field.Description className={css.description}>
            {description}
          </Field.Description>
        )}
        {typeof error === "string" && (
          <Field.Error className={css.error} match>
            {error}
          </Field.Error>
        )}
      </Field.Root>
    );
  },
);

NumberInput.displayName = "NumberInput";

NumberInput.propTypes = {
  bare: PropTypes.bool,
  value: PropTypes.number,
  defaultValue: PropTypes.number,
  onValueChange: PropTypes.func,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(["any"])]),
  smallStep: PropTypes.number,
  largeStep: PropTypes.number,
  format: PropTypes.object,
  locale: PropTypes.string,
  allowWheelScrub: PropTypes.bool,
  allowOutOfRange: PropTypes.bool,
  /* Off for values typed rather than nudged, such as prices. Base UI's numeric keypad has
     no decimal point on most phones, so currency asks for the decimal one. */
  steppers: PropTypes.bool,
  label: PropTypes.string,
  description: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  readOnly: PropTypes.bool,
  name: PropTypes.string,
  className: PropTypes.string,
};

export default NumberInput;
